import type { KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { notificationsApi, type Notification } from '@/api/notifications';
import { MIcon, MTag } from '@/components/mobile/MobileKit';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { describeNotification, type NotificationRole } from '@/lib/notificationKinds';
import { humanizeError } from '@/lib/errors';
import { cn } from '@/lib/utils';

/**
 * Лента уведомлений — одна на ученика и куратора (11.09.2026).
 *
 * Вынесена из страницы ученика, когда ленту получил куратор: строка, отметка
 * прочтения и пустое состояние живут в одном месте, а не расходятся по двум
 * копиям. Роль решает только адреса переходов (`describeNotification`) и текст
 * пустой ленты.
 *
 * Все отметки сбрасывают ключ `['notifications']` целиком — под ним и список, и
 * цифра непрочитанных в меню куратора (`['notifications','unread']`).
 */

// Некоторые уведомления приходят с незаполненными плейсхолдерами шаблона
// (например «{{student_name}}»). Чистим их, чтобы не показывать «программистский»
// текст. (Корневая причина — на бэкенде, см. backendGaps.)
function cleanTemplate(text: string): string {
  return text.replace(/\{\{\s*[\w.]+\s*\}\}/g, '…').replace(/\s+…/g, ' …').trim();
}

/**
 * `rows` — телефон сотрудника (13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §4.7): один блок строками с точкой «новое»
 * вместо карточки на каждое уведомление. Переходы и отметки — те же; ученическая лента прежняя.
 */
export function NotificationFeed({ role, rows = false }: { role: NotificationRole; rows?: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['notifications', 'list'], queryFn: () => notificationsApi.list() });
  const markOne = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  return (
    <>
      {markOne.isError && (
        <Card variant="subtle" className="mb-2 text-sm text-error">
          {humanizeError(markOne.error)}
        </Card>
      )}
      {list.isLoading ? (
        <Card variant="subtle" className="flex items-center justify-center gap-2 py-6 text-on-surface-variant">
          <span className="material-symbols-outlined animate-spin">progress_activity</span>
          {t('common.loading')}
        </Card>
      ) : list.isError ? (
        <Card variant="subtle" className="text-center text-error">{humanizeError(list.error)}</Card>
      ) : !list.data?.items.length ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {role === 'curator' ? t('notifications.emptyCurator') : t('notifications.empty')}
        </Card>
      ) : rows ? (
        <ul className="rounded-[26px] bg-surface-container-low px-4">
          {list.data.items.map((n, i, all) => (
            <NotificationRow
              key={n.id}
              n={n}
              role={role}
              onRead={() => !n.is_read && markOne.mutate(n.id)}
              asRow
              last={i === all.length - 1}
            />
          ))}
        </ul>
      ) : (
        <ul className="space-y-2">
          {list.data.items.map((n) => (
            <NotificationRow key={n.id} n={n} role={role} onRead={() => !n.is_read && markOne.mutate(n.id)} />
          ))}
        </ul>
      )}
    </>
  );
}

function NotificationRow({
  n,
  role,
  onRead,
  asRow = false,
  last = false,
}: {
  n: Notification;
  role: NotificationRole;
  onRead: () => void;
  asRow?: boolean;
  last?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Свой вид — только у известных типов; у прочих прежние значок и текст с
  // сервера, а нажатие лишь снимает «новое».
  const view = describeNotification(n, role);
  const activate = () => {
    onRead();
    if (view.to) navigate(view.to);
  };
  const linkProps = view.to
    ? {
        role: 'link',
        tabIndex: 0,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter') activate();
        },
      }
    : {};
  if (asRow) {
    return (
      <li data-notification={n.id} className={cn(!last && 'border-b border-outline')}>
        <div
          className={cn('flex items-start gap-3 py-3', (!n.is_read || view.to) && 'cursor-pointer')}
          onClick={activate}
          {...linkProps}
        >
          <MIcon icon={view.icon} tone={n.is_read ? 'muted' : 'warm'} />
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold leading-snug text-on-surface">
              {view.titleKey ? t(view.titleKey) : cleanTemplate(n.title)}
            </p>
            <p className="text-[12px] text-on-surface-variant">{view.detail ?? view.lessonTitle ?? cleanTemplate(n.body)}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {view.resubmitted && <MTag tone="warm">{t('notifications.resubmitted')}</MTag>}
              <span className="text-[11px] text-on-surface-variant">{new Date(n.created_at).toLocaleString(i18n.language)}</span>
            </div>
          </div>
          {/* «Новое» — точкой, а не плашкой: в строке 390 px плашка отнимала место у названия урока. */}
          {!n.is_read && (
            <span role="img" aria-label={t('notifications.new')} className="mt-1.5 h-2.5 w-2.5 flex-none rounded-full bg-primary" />
          )}
        </div>
      </li>
    );
  }
  return (
    <li data-notification={n.id}>
      <Card variant={n.is_read ? 'subtle' : 'default'}>
        <div
          className={cn('flex items-start gap-3', (!n.is_read || view.to) && 'cursor-pointer')}
          onClick={activate}
          {...linkProps}
        >
          <span className="material-symbols-outlined text-primary" aria-hidden="true">{view.icon}</span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-on-surface">{view.titleKey ? t(view.titleKey) : cleanTemplate(n.title)}</p>
            <p className="text-sm text-on-surface-variant">{view.detail ?? view.lessonTitle ?? cleanTemplate(n.body)}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {view.resubmitted && <MTag tone="warm">{t('notifications.resubmitted')}</MTag>}
              <span className="text-xs text-on-surface-variant">{new Date(n.created_at).toLocaleString(i18n.language)}</span>
            </div>
          </div>
          {!n.is_read && <Pill variant="primary">{t('notifications.new')}</Pill>}
        </div>
      </Card>
    </li>
  );
}
